/* eslint-disable max-lines */
import StepperReview from 'components/shared/Stepper/components/Review/StepperReview';
import OnboardingPayers from 'components/practice/Settings/components/Payers/OnboardingPayers';
import StepPlaceholder from 'components/shared/Stepper/components/StepPlaceholder/StepPlaceholder';
import OnboardingPackets from 'components/practice/Settings/components/Forms/FormPackets/OnboardingPackets';

import IntroStep from '../components/IntroStep/IntroStep';
import withWizardStep from '../components/withWizardStep';
import OnboardingRoles from '../../../Roles/OnboardingRoles';
import DataExport from '../components/DataExport/DataExport';
import AddressForm from '../components/AddressForm/AddressForm';
import SupportForm from '../components/SupportForm/SupportForm';
import WizardStaff from '../components/WizardStaff/WizardStaff';
import BillingTable from '../components/BillingTable/BillingTable';
import WizardKiosks from '../components/WizardKiosks/WizardKiosks';
import TrainingVideo from '../components/TrainingVideo/TrainingVideo';
import ScheduleOfFees from '../components/ScheduleOfFees/ScheduleOfFees';
import DelegationTasks from '../components/DelegationTasks/DelegationTasks';
import MainDetailsForm from '../components/MainDetailsForm/MainDetailsForm';
import ContactInfoForm from '../components/ContactInforForm/ContactInfoForm';
import AdminOrganizations from '../components/Organization/AdminOrganization';
import Responsibilities from '../components/Acknowledgements/Responsibilities';
import LiveTrainingStep from '../components/LiveTrainingStep/LiveTrainingStep';
import WizardEprescribe from '../components/WizardEprescribe/WizardEprescribe';
import OnboardingImports from '../components/OnboardingImports/OnboardingImports';
import PracticeSpecialty from '../components/PracticeSpecialty/PracticeSpecialty';
import TermsAndConditions from '../components/Acknowledgements/TermsAndConditions';
import PracticeBrandingForm from '../components/PracticeBrandingForm/PracticeBrandingForm';
import OnboardingIncomeIQueue from '../components/HealthcareProgram/OnboardingIncomeIQueue';
import OnboardingPaymentProgram from '../components/HealthcareProgram/OnboardingPaymentProgram';
import GoLiveDatesAndTrainings from '../components/GoLiveDatesAndTrainings/GoLiveDatesAndTrainings';
import OnboardingEligibilitySetup from '../components/HealthcareProgram/OnboardingEligibilitySetup';
import OnboardingFacilityTable from '../components/OnboardingFacilityTable/OnboardingFacilityTable';
import OnboardingClinicalForms from '../../../admin/pages/Practices/FormBuilder/OnboardingClinicalForms';
import ImportFromOrganization from '../components/OnboardingImports/components/ImportFromOrganization/ImportFromOrganization';

export default {
  onboarding__intro__intro: {
    component: IntroStep,
    headerOptions: {
      title: 'Welcome to Myriad Motion',
      description: 'Your onboarding journey starts here.'
    },
    smartSuggestions: [
      'Take a moment to explore what Myriad Motion offers and how it will transform your onboarding experience.',
      'This introduction sets the foundation for your personalized practice setup journey.',
      'Click "Start Myriad Motion" when you\'re ready to begin configuring your practice.'
    ]
  },
  onboarding__support_and_contact__support_and_contact: {
    component: withWizardStep(SupportForm),
    smartSuggestions: [
      'Your dedicated Myriad support team will assign consultants and onboarding specialists who will guide you through setup and ongoing success.',
      "If you don't see your contacts listed yet, they'll appear here once your setup team assigns them—no action is needed from your side.",
      'You can reach support anytime to explore or enable additional services like MedBill or the Virtual Office Manager.'
    ]
  },
  onboarding__go_live_dates_and_trainings__go_live_dates_and_trainings: {
    component: withWizardStep(GoLiveDatesAndTrainings),
    smartSuggestions: [
      'Review and confirm your go-live dates carefully — these mark when your practice officially starts using Myriad Health with real patient appointments.',
      'The hard launch date is your confirmed launch schedule; onboarding must be completed before this date to ensure a smooth transition.',
      'Acknowledge the training requirements to confirm that your team will attend all scheduled sessions.'
    ]
  },
  onboarding__kickoff_video_training__kickoff_video_training: {
    component: withWizardStep(TrainingVideo)
  },
  onboarding__front_office_video_training__front_office_video_training: {
    component: withWizardStep(TrainingVideo)
  },
  onboarding__clinical_video_training__clinical_video_training: {
    component: withWizardStep(TrainingVideo)
  },
  onboarding__back_office_video_training__back_office_video_training: {
    component: withWizardStep(TrainingVideo)
  },
  onboarding__hard_launch_video_training__hard_launch_video_training: {
    component: withWizardStep(TrainingVideo)
  },
  onboarding__acknowledgements__terms_and_conditions: {
    component: withWizardStep(TermsAndConditions),
    smartSuggestions: [
      'Review each section carefully to understand how MediPay and Myriad work together for your practice.',
      "Check all boxes once you've read and agreed to the listed responsibilities.",
      'Accepting these terms ensures your onboarding and payment setup continue without delays.'
    ]
  },
  onboarding__acknowledgements__responsibilities: {
    component: withWizardStep(Responsibilities),
    smartSuggestions: [
      'Set each due date to occur before its Live Training.',
      'To change a confirmed training date, contact your onboarding team.',
      'Being ready before training maximizes your time with our professionals.'
    ]
  },
  onboarding__basic_setup_confirmation__main_details: {
    component: withWizardStep(MainDetailsForm),
    smartSuggestions: [
      'Use your legal practice name as on IRS/payor records; add a DBA only if patients see a different name.',
      'Enter your specialty taxonomy and choose the correct Tax ID type—EIN for organizations, SSN/ITIN for solo providers—to ensure claims and 1099s match.',
      'Select the practice kind (e.g., Medical, Dental) to tailor forms, services, and reporting from the start.'
    ]
  },
  onboarding__basic_setup_confirmation__address: {
    component: withWizardStep(AddressForm),
    smartSuggestions: [
      'Enter the full street address of your primary practice location (include suite/unit if applicable).',
      'This address is used across setup—claims, billing, and patient messages—so ensure City, State, and ZIP are accurate.',
      'If you have multiple locations, start with your main site now; you can add more addresses later.'
    ]
  },
  onboarding__basic_setup_confirmation__contact_information: {
    component: withWizardStep(ContactInfoForm),
    smartSuggestions: [
      'Your text-messaging and voice number is generated automatically after your practice address is verified. If you see an error here, complete or correct the Address step.',
      'Fax is optional: click “Request Fax” to auto-provision a secure fax number only if your practice plans to send/receive faxes.',
      'Use a monitored practice email and main phone number; these drive patient notifications, reminders, and payer communications.'
    ]
  },
  onboarding__basic_setup_confirmation__branding: {
    component: withWizardStep(PracticeBrandingForm),
    smartSuggestions: [
      'Upload your practice logo—it will appear on receipts, emails, and patient-facing documents for consistent branding.',
      'Use a clear, square image (minimum 400x400px, PNG or JPEG) to ensure it displays well across all communications.',
      'Set your brand colors to match your logo; these will soon apply automatically to emails, patient portals, and printed materials.'
    ]
  },
  onboarding__basic_setup_confirmation__organization: {
    component: withWizardStep(AdminOrganizations),
    smartSuggestions: [
      'If your practice belongs to a larger group or network, our staff will link you automatically—no action needed right now.',
      "If you don't operate under any organization, click 'Skip Organization' to continue your setup independently.",
      'Once linked, review and confirm your organization details to ensure your practice information and reporting stay aligned.'
    ]
  },
  onboarding__clearinghouse__speciality: {
    component: withWizardStep(PracticeSpecialty),
    smartSuggestions: [
      'Your specialties determine how patient estimates, Good Faith Estimates (GFEs), and payer benefits are calculated.',
      'For best results, include multiple specialties — from most specific to most general (for example, list Orthopedics before General Medicine).'
    ]
  },
  onboarding__staff_and_permissions__roles_and_permissions: {
    component: withWizardStep(OnboardingRoles),
    smartSuggestions: [
      'Create and customize roles to match how your practice operates—each role can have its own set of permissions.',
      'You can add as many roles as needed, from clinical to administrative, and adjust access levels anytime.',
      'Once your team is added, assign users to these roles to control what each person can view or manage.'
    ]
  },
  onboarding__staff_and_permissions__staff_invitation: {
    component: withWizardStep(WizardStaff),
    smartSuggestions: [
      'Add your key team members first—each will receive an email invite to join with their assigned role.',
      'Prioritize staff who will help complete onboarding tasks, such as administrators, billers, or lead providers.',
      'Once added, your team can log in immediately and continue setup steps assigned to their roles.'
    ]
  },
  onboarding__delegation__delegation: {
    component: withWizardStep(DelegationTasks),
    smartSuggestions: [
      'Select any step or substep and assign it to a team member who will handle that part of the setup.',
      'You can also add a note or set a due date to give clear instructions and keep tasks on schedule.',
      'Delegate multiple steps or substeps to different users to share the workload and ensure everything is completed on time.'
    ]
  },
  onboarding__data_export__data_export: {
    component: withWizardStep(DataExport),
    smartSuggestions: [
      'Request the most comprehensive data export available from your previous system to ensure all critical patient information transfers smoothly.',
      'If you need help with data conversion or mapping, our team offers professional data migration services to handle complex transitions.',
      'Make sure your exported data is as fresh and complete as possible before uploading — this ensures accurate patient records from day one.'
    ]
  },
  onboarding__data_import__organization: {
    component: withWizardStep(ImportFromOrganization),
    smartSuggestions: [
      'Select a location from your organization to import existing data like patients, services, macros, packages, and products.',
      'Choose "All data" to import everything at once, or select "Specific data" to pick exactly what you need.',
      'You can skip importing if you prefer to start fresh with your new practice setup.'
    ]
  },
  onboarding__data_import__external: {
    component: withWizardStep(OnboardingImports),
    smartSuggestions: [
      "Select how you'd like to import your existing patient data — either upload a CSV file or use a software export from your previous system.",
      'Enter the estimated number of patients and double-check your file for accuracy before uploading to ensure smooth onboarding and correct billing data.',
      'You can review past imports or check supported EHR systems anytime to confirm compatibility and track which records were successfully added.'
    ]
  },
  onboarding__e_prescribe_setup__e_prescribe_setup: {
    component: withWizardStep(WizardEprescribe),
    smartSuggestions: [
      'Choose whether your practice will use e-prescribing to manage prescriptions electronically.',
      'To set up e-prescribing, activate it for your practice, then add each licensed provider.',
      "If your practice doesn't need e-prescribing, you can skip this step and enable it later from the Practice Hub."
    ]
  },
  onboarding__clearinghouse__billing_providers: {
    component: withWizardStep(BillingTable),
    smartSuggestions: [
      'Add a billing provider profile for every Tax ID and NPI combination you use for claims, whether group, facility or individual. Make sure the name, Tax ID, NPI, taxonomy and address exactly match your payer enrollment forms.',
      'Each Billing Provider profile populates the Billing Provider section of your CMS-1500 claim and is used by payers to validate your submissions.',
      'If you are unsure which Tax ID and NPI combination to use for a Billing Provider profile, check your current CMS-1500 claims or payer enrollment letters before continuing.'
    ]
  },
  onboarding__clearinghouse__facilities: {
    component: withWizardStep(OnboardingFacilityTable),
    smartSuggestions: [
      'Add every location where your practice delivers care, including satellite offices, surgery centers and off-site locations that appear on claims. Use the exact facility name, NPI (if assigned) and address that payers have on file so your CMS-1500 Service Facility Location (Box 32) matches your enrollment records.',
      'If your practice operates in multiple locations, add every location where care is delivered. This ensures billing, reporting and clearinghouse routing remain accurate from day one.'
    ]
  },
  onboarding__clearinghouse__payers: {
    component: withWizardStep(OnboardingPayers),
    smartSuggestions: [
      'Use the Add Payer… search bar to add all payers your practice bills today, including commercial plans, Medicare, Medicaid and secondary payers. These selections control where your claims, eligibility checks and ERAs are routed through the clearinghouse.',
      'If a payer is not listed, submit a request for a new payer ID through Myriad Support. Once approved, it will appear in your search results and can be added to your list. ',
      'Before moving on to EDI Enrollment, confirm that every active payer your practice bills is included here. This keeps claim submission and payment processing smooth. '
    ]
  },
  onboarding__clearinghouse__edi_enrollment: {
    component: withWizardStep(StepPlaceholder),
    smartSuggestions: [
      'EDI enrollment is crucial for going fully live on Myriad Health. Submit enrollments for every payer your practice bills and for each billing provider that sends claims.',
      'Until EDI is approved, payers may still send ERAs to your previous system. ',
      'Once enrollment is processed, ERAs will usually post only to Myriad Health Billing.',
      'EDI enrollment approval process can take a few days up to three weeks, depending on the payer.',
      'During this window, our expert billing support team is available to help you track enrollment status, confirm where ERAs are posting and know exactly when each payer is live. If you have any questions or concerns, reach out. You are very close to using Myriad Health as your full daily system. '
    ]
  },
  onboarding__healthcare_program__eligibility_setup: {
    component: withWizardStep(OnboardingEligibilitySetup),
    smartSuggestions: [
      'Set the assurance rating threshold that determines when a Good Faith Estimate is auto-finalized versus routed for review.',
      'Configure each eligibility workflow step and assign a team member to be alerted when manual attention is needed.',
      'These settings drive how patient benefits are verified before every encounter — review them with your billing lead.'
    ]
  },
  onboarding__healthcare_program__payment_program: {
    component: withWizardStep(OnboardingPaymentProgram),
    smartSuggestions: [
      'Define how your practice collects payments for in-network, out-of-network, and self-pay scenarios across each care phase.',
      'Use the live preview to see exactly what patients will experience as you adjust the payment configuration.',
      'Authorization, card-on-file, and charge limits set here apply automatically during checkout and recurring billing.'
    ]
  },
  onboarding__healthcare_program__incomeiqueue_rcm: {
    component: withWizardStep(OnboardingIncomeIQueue),
    smartSuggestions: [
      'Choose the automated RCM actions that should appear as suggestions in your IncomeIQueue for each network scenario.',
      'Suggestions remain inactive until you act on them — select all or a subset of encounters and apply actions in seconds.',
      'Tune these per network type so your team sees the right next step based on the patient agreement.'
    ]
  },
  onboarding__schedule_of_fees__schedule_of_fees: {
    component: withWizardStep(ScheduleOfFees),
    smartSuggestions: [
      'Start by selecting your practice specialty — this determines which services will appear in your Schedule of Fees.',
      'Choose the services you want to include, then review or edit their pricing before adding them to your practice.',
      'You can also apply a Fees Multiplier to adjust all service prices at once, helping you align your rates with your billing goals.'
    ]
  },
  onboarding__custom_forms__clinical_forms: {
    component: withWizardStep(OnboardingClinicalForms),
    smartSuggestions: [
      "Create and customize clinical forms to fit your practice's workflow — start by naming your form and defining its purpose.",
      'You can organize your form with categories and sections to collect the right information efficiently during patient encounters.',
      'Forms can also be grouped and used together for a smoother experience when managing related clinical or intake tasks.'
    ]
  },
  onboarding__custom_forms__form_packets: {
    component: withWizardStep(OnboardingPackets)
  },
  onboarding__kiosk_setup__kiosk_setup: {
    component: withWizardStep(WizardKiosks),
    smartSuggestions: [
      'Register and manage all kiosks used in your practice to streamline patient check-ins and other front-desk activities.',
      'You can add a new kiosk by entering its verification code, naming it, and assigning staff members who will manage its requests.',
      'Each kiosk can be edited, activated, or disabled at any time — keeping full control over which terminals are paired and in use.'
    ]
  },
  onboarding__kickoff_live_training__kickoff_live_training: {
    component: withWizardStep(LiveTrainingStep),
    smartSuggestions: [
      'Confirm your setup is complete before the kickoff call to maximize training time.',
      'Create a test patient that all staff members can use during training sessions.',
      'Ensure staff permissions and assignments are set up correctly before the call.'
    ]
  },
  onboarding__front_office_live_training__front_office_live_training: {
    component: withWizardStep(LiveTrainingStep),
    smartSuggestions: [
      'Complete healthcare program setup including GFE and Pre-authorized healthcare before training.',
      'Test your card reader and virtual terminal to ensure smooth payment processing.',
      'Upload your schedule of fees and set up self-scheduling if applicable.'
    ]
  },
  onboarding__clinical_live_training__clinical_live_training: {
    component: withWizardStep(LiveTrainingStep),
    smartSuggestions: [
      'Ensure your clinical forms and templates are built and verified by the principal user.',
      'Add codes and products if 3D charting is enabled to enable the checkout flow.',
      'Watch the product tour video before the training session.'
    ]
  },
  onboarding__back_office_live_training__back_office_live_training: {
    component: withWizardStep(LiveTrainingStep),
    smartSuggestions: [
      'Submit EDI enrollments and verify your schedule of fees before the billing training.',
      'Ensure the full data import is verified by the principal user.',
      'Practice filing a test claim and running a patient payment before the session.'
    ]
  },
  onboarding__hard_launch_live_training__hard_launch_live_training: {
    component: withWizardStep(LiveTrainingStep),
    smartSuggestions: [
      'Ensure 100% of onboarding and training is complete before hard launch.',
      'Your practice should be ready for live charting and patient payments.',
      'This is the final training before going fully live with Myriad Health.'
    ]
  },
  onboarding__review_review: {
    component: withWizardStep(StepperReview),
    headerOptions: {
      title: 'Review',
      description: 'Final review of all onboarding steps before going live.'
    }
  }
};
